import { useEffect, useSyncExternalStore } from "react"
import Head from "next/head"

import { siteConfig } from "@/config/site"

interface BrowserTabTitleProps {
  type: string
  name?: string
}

// next/head keeps whichever <title> registered last, and the layout's default
// can register after the page's (a remounted layout re-registers). Counting the
// mounted page titles lets the layout drop its default instead of racing them.
let mountedPageTitles = 0
const pageTitleListeners = new Set<() => void>()

const subscribeToPageTitles = (listener: () => void) => {
  pageTitleListeners.add(listener)
  return () => {
    pageTitleListeners.delete(listener)
  }
}

const changeMountedPageTitles = (delta: number) => {
  mountedPageTitles += delta
  pageTitleListeners.forEach((listener) => listener())
}

/** True while any BrowserTabTitle is mounted, so a layout can omit its default title. */
export const useHasPageTitle = () =>
  useSyncExternalStore(
    subscribeToPageTitles,
    () => mountedPageTitles > 0,
    () => false,
  )

/**
 * Component to set a descriptive page title.
 * Usage:
 *   <BrowserTabTitle type="Run" name="abc123" />  // "Run > abc123 | Coval"
 *   <BrowserTabTitle type="Runs" />                // "Runs | Coval"
 *
 * The title will be formatted as "{type} > {name} | {siteConfig.name}" if name is provided,
 * or "{type} | {siteConfig.name}" if name is not provided.
 */
export const BrowserTabTitle = ({ type, name }: BrowserTabTitleProps) => {
  const fullTitle = name
    ? `${type} > ${name} | ${siteConfig.name}`
    : `${type} | ${siteConfig.name}`

  useEffect(() => {
    changeMountedPageTitles(1)
    return () => changeMountedPageTitles(-1)
  }, [])

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta key="og:title" content={fullTitle} property="og:title" />
    </Head>
  )
}
